import React, { useEffect, useState } from 'react';

import { useAppDispatch } from '../../../redux/hooks';
import { setNotificationMessage } from '../../../redux/slices/NotificationManagementSlice';
import MyButton from '../../../shared-components/button/Button';
import CustomInput from '../../../shared-components/custom-input/CustomInput';
import ModalBox from '../../../shared-components/modal-box/ModalBox';
import SwitchButton from '../../../shared-components/switch-button-field/SwitchButton';
import getErrorMessage from '../../../shared-functions/getErrorMessage';
import ocpiApi from '../../../utils/api/ocpi';

interface FieldConfig {
  name: string;
  enabled: boolean;
  alias: string; // '' means "use canonical field name" (no custom alias)
}

interface ModuleConfig {
  module: string;
  fields: FieldConfig[];
}

interface AdditionalFieldsModalProperties {
  open: boolean;
  onClose: () => void;
  role: string;
  partyId: string;
  countryCode: string;
}

/**
 * Normalize a server field into local state. The backend always returns an
 * alias (defaulting to the canonical name when unset), so an alias equal to
 * the field name is treated as "no custom alias" (empty input).
 * @param {any} field - Raw field from the API.
 */
const normalizeField = (field: any): FieldConfig => ({
  name: field.name,
  enabled: !!field.enabled,
  alias: field.alias && field.alias !== field.name ? field.alias : '',
});

/**
 * Modal for configuring a tenant's per-module additional fields
 * (enable/disable + alias key). Save is per module.
 * @param {any} props - Component props.
 * @param {any} props.open - Open state.
 * @param {any} props.onClose - Close callback.
 * @param {any} props.role - Tenant role (CPO | EMSP).
 * @param {any} props.partyId - Tenant party ID.
 * @param {any} props.countryCode - Tenant country code.
 * @returns {any} Rendered component.
 */
function AdditionalFieldsModal({
  open,
  onClose,
  role,
  partyId,
  countryCode,
}: AdditionalFieldsModalProperties): React.ReactElement {
  const dispatch = useAppDispatch();
  const [loading, setLoading] = useState(false);
  const [modules, setModules] = useState<ModuleConfig[]>([]);
  const [original, setOriginal] = useState<ModuleConfig[]>([]);
  const [savingModule, setSavingModule] = useState<string | null>(null);

  /**
   * Fetch additional-field configuration for every module of this tenant.
   */
  const fetchConfig = async () => {
    setLoading(true);
    try {
      const response = await ocpiApi.getAdditionalFields(role, countryCode, partyId);
      const raw = response.data?.data || [];
      const normalized: ModuleConfig[] = raw.map((m: any) => ({
        module: m.module,
        fields: (m.fields || []).map(normalizeField),
      }));
      setModules(normalized);
      setOriginal(JSON.parse(JSON.stringify(normalized)));
    } catch (error: any) {
      dispatch(
        setNotificationMessage({
          status: true,
          message: getErrorMessage(error, 'Failed to load configurations'),
          type: 'error',
        }),
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open) {
      fetchConfig();
    }
  }, [open, role, partyId, countryCode]);

  /**
   * Patch a single field within a module in local state.
   * @param {any} moduleName - The module the field belongs to.
   * @param {any} fieldName - The field to update.
   * @param {any} patch - Partial field changes.
   */
  const updateField = (moduleName: string, fieldName: string, patch: Partial<FieldConfig>) => {
    setModules((previous) =>
      previous.map((m) =>
        m.module !== moduleName
          ? m
          : {
              ...m,
              fields: m.fields.map((f) => (f.name !== fieldName ? f : { ...f, ...patch })),
            },
      ),
    );
  };

  /**
   * Whether a module has unsaved changes compared to the last loaded state.
   * @param {any} moduleName - The module to check.
   */
  const isModuleDirty = (moduleName: string): boolean => {
    const current = modules.find((m) => m.module === moduleName);
    const base = original.find((m) => m.module === moduleName);
    return JSON.stringify(current) !== JSON.stringify(base);
  };

  /**
   * Persist a single module's field configuration.
   * @param {any} moduleName - The module to save.
   */
  const handleSaveModule = async (moduleName: string) => {
    const module = modules.find((m) => m.module === moduleName);
    if (!module) return;

    // Only send an alias when the field is enabled and a custom (non-canonical)
    // key was entered; otherwise omit it so the backend keeps its current value.
    const fields = module.fields.map((f) => {
      const alias = f.alias.trim();
      const entry: { name: string; enabled: boolean; alias?: string } = {
        name: f.name,
        enabled: f.enabled,
      };
      if (f.enabled && alias && alias !== f.name) {
        entry.alias = alias;
      }
      return entry;
    });

    setSavingModule(moduleName);
    try {
      await ocpiApi.updateAdditionalFields(role, countryCode, partyId, moduleName, fields);
      dispatch(
        setNotificationMessage({
          status: true,
          message: `${moduleName} configuration saved`,
          type: 'success',
        }),
      );
      // Sync the saved module into the baseline so it is no longer marked dirty.
      setOriginal((previous) =>
        previous.map((m) => (m.module === moduleName ? JSON.parse(JSON.stringify(module)) : m)),
      );
    } catch (error: any) {
      dispatch(
        setNotificationMessage({
          status: true,
          message: getErrorMessage(error, `Failed to save ${moduleName} configuration`),
          type: 'error',
        }),
      );
    } finally {
      setSavingModule(null);
    }
  };

  /**
   * Render the modal body.
   */
  const renderContent = () => {
    if (loading) {
      return <div className="ocpi-config-modal__state">Loading configurations...</div>;
    }

    if (modules.length === 0) {
      return <div className="ocpi-config-modal__state">No configurable fields available.</div>;
    }

    return (
      <div className="ocpi-config-modal">
        <p className="ocpi-config-modal__intro">
          Enable the additional fields you want to exchange. When enabled, you may set an alias —
          the key name your system uses for that field. Leave the alias blank to use the default
          field name.
        </p>

        {modules.map((eachModule) => {
          const dirty = isModuleDirty(eachModule.module);
          const saving = savingModule === eachModule.module;

          return (
            <div className="ocpi-config-modal__module" key={eachModule.module}>
              <div className="ocpi-config-modal__module-header">
                <h3 className="ocpi-config-modal__module-title">{eachModule.module.toUpperCase()}</h3>
                <MyButton
                  label={saving ? 'Saving...' : 'Save'}
                  buttonType="primary"
                  onClickFunc={() => handleSaveModule(eachModule.module)}
                  disableButton={saving || !dirty}
                />
              </div>

              <div className="ocpi-config-modal__fields">
                <div className="ocpi-config-modal__row ocpi-config-modal__row--head">
                  <span className="ocpi-config-modal__col-field">Field</span>
                  <span className="ocpi-config-modal__col-toggle">Enabled</span>
                  <span className="ocpi-config-modal__col-alias">Alias key</span>
                </div>

                {eachModule.fields.map((field) => (
                  <div className="ocpi-config-modal__row" key={field.name}>
                    <span className="ocpi-config-modal__col-field">{field.name}</span>
                    <span className="ocpi-config-modal__col-toggle">
                      <SwitchButton
                        checked={field.enabled}
                        change={(checked) =>
                          updateField(eachModule.module, field.name, { enabled: checked })
                        }
                      />
                    </span>
                    <span className="ocpi-config-modal__col-alias">
                      <CustomInput
                        inputLabel=""
                        type="text"
                        name={`${eachModule.module}-${field.name}-alias`}
                        placeholder={field.name}
                        value={field.alias}
                        disabled={!field.enabled}
                        onChangeValue={(_, value) =>
                          updateField(eachModule.module, field.name, { alias: value as string })
                        }
                      />
                    </span>
                  </div>
                ))}
              </div>
            </div>
          );
        })}

        <div className="ocpi-config-modal__actions">
          <MyButton label="Close" buttonType="outline" onClickFunc={onClose} />
        </div>
      </div>
    );
  };

  return (
    <ModalBox
      open={open}
      title="Additional Field Configurations"
      content={renderContent()}
      closeModal={onClose}
      header
      rightCorner=""
    />
  );
}

export default AdditionalFieldsModal;
